import { Popover } from '@mui/material';
import moment from 'moment';
import { useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import { twMerge } from 'tailwind-merge';

import CalendarIcon from '../../../assets/svg/CalendarIcon';
import CrossIcon from '../../../assets/svg/CrossIcon';
import { DATE_TIME_FORMAT } from '../../../constants';
import { Button } from '../../Shared';

import './react-date-picker.css';

const DEFAULT_TITLE = 'Today';

const isSameDate = (date, comparedDate) =>
  moment(date).isSame(comparedDate, 'day');

const getTitle = (startDate, endDate) => {
  const isStartDateToday = isSameDate(startDate, new Date());
  const isEndDateToday = isSameDate(endDate, new Date());
  const isToday = isStartDateToday && isEndDateToday;

  if (isToday) {
    return DEFAULT_TITLE;
  }

  const isSameStartEndDate = isSameDate(startDate, endDate);

  if (isSameStartEndDate) {
    return `${moment(startDate).format(DATE_TIME_FORMAT.MMM_D_YYYY)}`;
  }

  return `${moment(startDate).format(DATE_TIME_FORMAT.MMM_D_YYYY)} - ${moment(endDate).format(DATE_TIME_FORMAT.MMM_D_YYYY)}`;
};

const getButtonDoneDisabledState = (
  startDate,
  endDate,
  currStartDate,
  currEndDate,
) => {
  const hasCurrEndDate = !!currEndDate;
  const isSameStartDate = isSameDate(startDate, currStartDate);
  const isSameStartEndDate = isSameDate(startDate, endDate);

  if (!hasCurrEndDate && isSameStartDate && isSameStartEndDate) return true;

  const isSameEndDate = isSameDate(endDate, currEndDate);

  return isSameStartDate && isSameEndDate;
};

export const DateFilter = ({ startDate, endDate, onChangeDateRange }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [currStartDate, setCurrStartDate] = useState(new Date(startDate));
  const [currEndDate, setCurrEndDate] = useState(new Date(endDate));

  const onChangeCurrDate = ([nextStartDate, nextEndDate]) => {
    setCurrStartDate(nextStartDate);
    setCurrEndDate(nextEndDate);
  };

  const onChange = () => {
    const nextStartDate = moment(currStartDate).format(
      DATE_TIME_FORMAT.YYYY_MM_DD_DASH,
    );
    const nextEndDate = moment(currEndDate ?? currStartDate).format(
      DATE_TIME_FORMAT.YYYY_MM_DD_DASH,
    );

    onChangeDateRange(prev => {
      return {
        ...prev,
        startDate: nextStartDate,
        endDate: nextEndDate,
      };
    });
    onClosePopover();
  };

  const reset = () => {
    const nextDate = moment(new Date()).format(
      DATE_TIME_FORMAT.YYYY_MM_DD_DASH,
    );

    onChangeDateRange(prev => {
      return {
        ...prev,
        startDate: nextDate,
        endDate: nextDate,
      };
    });
  };

  const onOpenPopover = e => {
    if (e.target.localName !== 'button') return;
    setCurrStartDate(new Date(startDate));
    setCurrEndDate(new Date(endDate));
    setAnchorEl(e.currentTarget);
  };

  const onClosePopover = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const title = getTitle(startDate, endDate);
  const isToday = title === DEFAULT_TITLE;

  const isButtonDoneDisabled = getButtonDoneDisabledState(
    startDate,
    endDate,
    currStartDate,
    currEndDate,
  );

  return (
    <>
      <button
        className={twMerge(
          'flex items-center gap-2 rounded-full py-[11px] pl-5 pr-6 text-brand-900 border border-brand-900 font-medium text-[16px]/[24px] focus-visible:outline-none',
          !isToday && 'bg-brand-900 text-white',
        )}
        onClick={onOpenPopover}
      >
        <CalendarIcon
          className={twMerge(
            'w-4 h-4 text-brand-500',
            !isToday && 'text-brand-300',
          )}
        />
        {title}
        {!isToday && (
          <div onClick={reset}>
            <CrossIcon className="w-4 h-4 text-white" />
          </div>
        )}
      </button>
      <Popover
        open={open}
        slotProps={{
          paper: {
            className:
              'leading-[0] !rounded-xl mt-2 !shadow-[0_4px_20px_0_rgba(17,17,58,0.08)] bg-white !overflow-auto',
          },
        }}
        anchorEl={anchorEl}
        onClose={onClosePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <ReactDatePicker
          selected={currStartDate}
          startDate={currStartDate}
          endDate={currEndDate}
          onChange={onChangeCurrDate}
          monthsShown={2}
          selectsRange
          inline
          calendarClassName="datepicker"
        />
        <div className="px-5 py-3 border-t border-t-base-200">
          <Button
            title="Done"
            onClick={onChange}
            disabled={isButtonDoneDisabled}
            className="ml-auto"
          />
        </div>
      </Popover>
    </>
  );
};
